import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
} from '@mui/material';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import { api } from '../../services/api';

interface BPointPaymentProps {
  orderId: number;
  amount: number;
  /** Delivery fee (so backend can persist before payment; avoids overpaid status when callback runs) */
  deliveryFee?: number;
  /** Voucher amount (so backend can persist before payment; avoids wrong payable at callback) */
  voucherAmount?: number;
  /** Parcel day and slot (so backend can persist before payment; order still has parcel day if user never clicks Place Order) */
  parcelDayId?: number | null;
  parcelDayPickupSlotId?: number | null;
  deliveryMethod?: 'pickup' | 'delivery';
  onSuccess: (transactionId: string, receiptNumber: string) => void;
  onCancel: () => void;
  onError: (error: string) => void;
}

const BPOINT_POPUP_NAME = 'bpoint_payment';

/**
 * BPOINT payment: opens BPOINT hosted page in a popup (no iframe) due to X-Frame-Options.
 * When the user finishes payment, the popup is redirected to our site and notifies this
 * window via postMessage, then closes. If the popup is blocked, falls back to same-tab redirect.
 */
const BPointPayment: React.FC<BPointPaymentProps> = ({
  orderId,
  amount,
  deliveryFee,
  voucherAmount,
  parcelDayId,
  parcelDayPickupSlotId,
  deliveryMethod,
  onError,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handlePayNow = async () => {
    try {
      setLoading(true);
      setError(null);

      // Round to 2 decimals to avoid floating-point precision issues (e.g. with voucher: 28.849999999994)
      const round2 = (n: number) => Math.round(n * 100) / 100;
      const payload: Record<string, number | string> = {
        order_id: orderId,
        amount: round2(amount),
      };
      if (deliveryFee !== undefined && deliveryFee !== null) {
        payload.delivery_fee = round2(deliveryFee);
      }
      if (voucherAmount !== undefined && voucherAmount !== null) {
        payload.voucher_amount = round2(voucherAmount);
      }
      if (parcelDayId != null && parcelDayId > 0) {
        payload.parcel_day_id = parcelDayId;
        if (deliveryMethod === 'pickup' && parcelDayPickupSlotId != null && parcelDayPickupSlotId > 0) {
          payload.parcel_day_pickup_slot_id = parcelDayPickupSlotId;
        }
        if (deliveryMethod === 'pickup' || deliveryMethod === 'delivery') {
          payload.delivery_method = deliveryMethod;
        }
      }

      const response = await api.post('/v1/payment/bpoint/create-token', payload);

      if (response.data.success && response.data.data?.url) {
        const url = response.data.data.url;
        // Prefer popup so the user stays on checkout; fall back to same-tab if blocked
        const popup = window.open(
          url,
          BPOINT_POPUP_NAME,
          'width=600,height=700,scrollbars=yes,resizable=yes'
        );
        if (popup) {
          // Popup opened; it will close and postMessage when payment is done
          setLoading(false);
          return;
        }
        // Popup blocked: fall back to same-tab redirect
        window.location.href = url;
        return;
      }

      const errorMsg = response.data.message || 'Failed to initiate payment';
      setError(errorMsg);
      onError(errorMsg);
    } catch (err: any) {
      const errorMsg = err.response?.data?.message || 'Failed to initiate payment. Please try again.';
      setError(errorMsg);
      onError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Button
        variant="contained"
        size="large"
        fullWidth
        onClick={handlePayNow}
        disabled={loading}
        startIcon={loading ? <CircularProgress size={20} /> : <CreditCardIcon />}
        sx={{ py: 1.5 }}
      >
        {loading ? 'Processing...' : `Pay $${amount.toFixed(2)} by Card`}
      </Button>

      <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
        A secure payment window will open. If it doesn’t, please allow popups for this site.
      </Typography>
    </Box>
  );
};

export default BPointPayment;
